<template>
  <div class="column-mapper">
    <div class="column-mapper-header">
      <h3>Map CSV Columns to System Fields</h3>
      <div class="actions">
        <button @click="autoMapColumns" class="btn btn-secondary">Auto-Map</button>
        <button @click.stop="showSaveDialog" class="btn btn-primary">Save Mapping</button>
        <button @click.stop="showLoadDialog" class="btn btn-primary">Load Saved Mapping</button>
      </div>
    </div>

    <div class="mapping-summary" :class="{ 'complete': allRequiredMapped }">
      <div class="summary-content">
        <strong>{{ mappedRequiredCount }} of {{ totalRequiredCount }}</strong> required fields mapped
        <ul v-if="unmappedRequired.length" class="unmapped-list">
          <li v-for="field in unmappedRequired" :key="field.value">{{ field.label }}</li>
        </ul>
      </div>
      <div v-if="allRequiredMapped" class="complete-indicator">
        <i class="fa fa-check-circle"></i>
      </div>
    </div>
    
    <div class="data-table">
      <table>
        <thead>
          <tr>
            <th
              v-for="header in headers"
              :key="header"
              :class="getColumnClass(header)"
            >
              <div class="column-header">
                <div class="csv-column">{{ header }}</div>
                <div class="select-container">
                  <select
                    v-model="columnMapping[header]"
                    class="mapping-select"
                    :aria-label="`Map ${header} column to system field`"
                  >
                    <option value="">-- Select field --</option>
                    <option
                      v-for="field in availableFields"
                      :key="field.value"
                      :value="field.value"
                    >
                      {{ field.label }} {{ field.required ? '(Required)' : '' }}
                    </option>
                  </select>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in displayData" :key="rowIndex">
            <td
              v-for="header in headers"
              :key="`${rowIndex}-${header}`"
              :class="getColumnClass(header)"
            >
              {{ row[header] }}
            </td>
          </tr>
        </tbody>
      </table>
      
      <div class="pagination">
        <button 
          @click="prevPage" 
          class="btn btn-sm" 
          :disabled="currentPage === 1"
        >
          Previous
        </button>
        <span class="page-info">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <button 
          @click="nextPage" 
          class="btn btn-sm" 
          :disabled="currentPage === totalPages"
        >
          Next
        </button>
      </div>
    </div>
    
    <mapping-save-modal
      ref="saveModal"
      :show="showSaveModal"
      :mapping="columnMapping"
      @close="showSaveModal = false"
      @save="saveMapping"
    />

    <mapping-load-modal
      :show="showLoadModal"
      @close="showLoadModal = false"
      @load="loadMapping"
    />
  </div>
</template>

<script>
import MappingSaveModal from './MappingSaveModal.vue';
import MappingLoadModal from './MappingLoadModal.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    MappingSaveModal,
    MappingLoadModal
  },
  
  props: {
    headers: {
      type: Array,
      required: true
    },
    previewData: {
      type: Array,
      required: true
    },
    availableFields: {
      type: Array,
      required: true
    }
  },
  
  data() {
    return {
      showSaveModal: false,
      showLoadModal: false
    }
  },
  
  computed: {
    ...mapGetters([
      'schoolId',
      'studentImportColumnMapping',
      'studentImportCurrentPage',
      'studentImportRowsPerPage'
    ]),
    
    columnMapping: {
      get() {
        return this.studentImportColumnMapping;
      },
      set(value) {
        this.$store.dispatch('SET_STUDENT_IMPORT_COLUMN_MAPPING', value);
      }
    },
    
    currentPage: {
      get() {
        return this.studentImportCurrentPage;
      },
      set(value) {
        this.$store.dispatch('SET_STUDENT_IMPORT_PAGE', value);
      }
    },
    
    rowsPerPage() {
      return this.studentImportRowsPerPage;
    },
    
    requiredFields() {
      return this.availableFields
        .filter(field => field.required)
        .map(field => field.value);
    },
    
    missingRequiredFields() {
      const mappedFields = Object.values(this.columnMapping);
      return this.requiredFields.filter(field => !mappedFields.includes(field))
        .map(field => {
          const fieldObj = this.availableFields.find(f => f.value === field);
          return fieldObj ? fieldObj.label : field;
        });
    },

    totalRequiredCount() {
      return this.requiredFields.length;
    },

    mappedRequiredCount() {
      const mappedFields = Object.values(this.columnMapping);
      return this.requiredFields.filter(field => mappedFields.includes(field)).length;
    },

    unmappedRequired() {
      const mappedFields = Object.values(this.columnMapping);
      return this.availableFields
        .filter(field => field.required && !mappedFields.includes(field.value));
    },

    allRequiredMapped() {
      return this.missingRequiredFields.length === 0;
    },

    totalPages() {
      return Math.ceil(this.previewData.length / this.rowsPerPage);
    },
    
    displayData() {
      const start = (this.currentPage - 1) * this.rowsPerPage;
      const end = start + this.rowsPerPage;
      return this.previewData.slice(start, end);
    }
  },
  
  methods: {
    isRequiredField(field) {
      return this.requiredFields.includes(field);
    },

    getColumnClass(header) {
      const mappedValue = this.columnMapping[header];
      if (mappedValue) {
        return 'column-cell mapped';
      }
      // Check if this column should have a required field mapped
      // For now, just return empty if not mapped
      return 'column-cell';
    },
    
    autoMapColumns() {
      const newMapping = {};
      
      this.headers.forEach(header => {
        // Try to find matching field by normalizing names
        const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');
        
        const match = this.availableFields.find(field => {
          const normalizedField = field.label.toLowerCase().replace(/[^a-z0-9]/g, '');
          return normalizedHeader === normalizedField ||
                 normalizedHeader.includes(normalizedField) ||
                 normalizedField.includes(normalizedHeader);
        });
        
        if (match) {
          newMapping[header] = match.value;
        }
      });
      
      this.columnMapping = { ...newMapping };
    },
    
    showSaveDialog() {
      this.showSaveModal = true;
    },
    
    showLoadDialog() {
      this.showLoadModal = true;
    },
    
    saveMapping(name) {
      this.$store.dispatch('SAVE_STUDENT_IMPORT_MAPPING', {
        school_id: this.schoolId,
        name: name,
        mapping: this.columnMapping
      })
      .then(response => {
        this.$refs.saveModal.resetSaving();
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'success',
          message: 'Mapping saved successfully'
        });
        this.showSaveModal = false;
      })
      .catch(error => {
        this.$refs.saveModal.resetSaving();
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Failed to save mapping: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
        });
      });
    },
    
    loadMapping(mapping) {
      this.columnMapping = mapping.mapping;
      this.showLoadModal = false;
    },
    
    getMappingForImport() {
      return this.columnMapping;
    },
    
    validateMapping() {
      return this.missingRequiredFields.length === 0;
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../../../assets/css/partials/variables';

.column-mapper-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.column-mapper h3 {
  margin: 0;
}

.actions {
  display: flex;
  gap: 8px;
}

.mapping-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 4px;
  margin-bottom: 24px;
  min-height: 56px;

  &.complete {
    background-color: rgba($green, 0.1);
    border-color: $green;
  }
}

.summary-content {
  flex: 1;
  font-size: 15px;
  line-height: 1.5;
}

.unmapped-list {
  margin: 12px 0 0 0;
  padding-left: 20px;
  font-size: 14px;
  color: #856404;

  li {
    margin-bottom: 4px;
  }

  .complete & {
    display: none;
  }
}

.complete-indicator {
  color: $green;
  font-size: 24px;
  margin-left: 16px;
}

.column-header {
  display: flex;
  flex-direction: column;
}

.csv-column {
  font-weight: bold;
  margin-bottom: 8px;
}

.mapping-select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  font-size: 14px;
}

.alert {
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.alert-warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
}

.data-table {
  overflow-x: auto;
  margin-top: 16px;
}

table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e0e0e0;
}

th, td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

th {
  background-color: #f5f5f5;
}

tr:nth-child(even) {
  background-color: #f9f9f9;
}

.column-cell {
  &.mapped {
    border-left: 3px solid $green;
  }

  &.required-unmapped {
    border-left: 3px solid $yellow;
  }
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 16px;
  gap: 12px;
}

.page-info {
  font-size: 14px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-primary {
  background-color: $green;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: $dark-green;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #757575;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.select-container {
  position: relative;
}

.select-container::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #555;
  pointer-events: none;
}
</style> 